import moment from 'moment'

import { Client } from '../../models/Client'
import { Dashboard } from '../../models/Dashboard'
import { RecordFactory } from '../../models/RecordFactory'
import { getViewTitleFromView } from '../../utils/dashboard'
import { e164ToPhone } from '../../utils/phone'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import {
    ClickableTableRow,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../app/design/Table'
import { DashboardSkeleton } from '../common/skeletons/DashboardSkeleton'
import { ClientTableCell } from './ClientTableCell'
import { EmptyDashboardLayout } from './EmptyDashboardLayout'
import { KeyMetric } from './KeyMetric'

type Props = {
    clients: Client[]
    view: number
    dashboard?: Dashboard
    searchText: string
    isLoading: boolean
}

const viewWillAppear = async (base: PocketComponent<Props>) => {
    return await Promise.all(
        base.props.clients.map((c) => RecordFactory.getInstance().loadClient(c.id!))
    )
}

function render({
    base: {
        props: { clients, view, dashboard, searchText, isLoading },
    },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>, Props>) {
    if (isLoading) {
        return <DashboardSkeleton />
    }

    return (
        <div className="flex flex-col gap-6">
            <KeyMetric
                metrics={[
                    {
                        title: `${getViewTitleFromView(dashboard, view)} ${
                            clients.length === 1 ? `client` : `clients`
                        }`,
                        value: (dashboard?.value ?? 0).toString(),
                    },
                ]}
            />
            <Table>
                <TableHeader>
                    <TableRow location="head" className="sticky top-0">
                        <TableHead className="sticky top-0 font-normal">Client</TableHead>
                        <TableHead className="sticky top-0 font-normal">Contact info</TableHead>
                        <TableHead className="sticky top-0 font-normal">Lead source</TableHead>
                        <TableHead className="sticky top-0 font-normal">Added</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {clients.map((c) => {
                        const user = c.person
                        return (
                            <ClickableTableRow key={c.id} href={`/clients/${c.id}`}>
                                <TableCell>
                                    <ClientTableCell user={user} />
                                </TableCell>
                                <TableCell>
                                    <div className="flex flex-col">
                                        <span>{user?.email}</span>
                                        <span>{e164ToPhone(user?.phone)}</span>
                                    </div>
                                </TableCell>
                                <TableCell>{c?.channel?.name ?? '-'}</TableCell>
                                <TableCell>{moment(c.createdDate).format('MMM D, YYYY')}</TableCell>
                            </ClickableTableRow>
                        )
                    })}
                    {clients.length === 0 && (
                        <TableCell colSpan={10}>
                            <EmptyDashboardLayout dashboard={dashboard} searchtext={searchText} />
                        </TableCell>
                    )}
                </TableBody>
            </Table>
        </div>
    )
}

export const ClientsDashboard = usePSComponent(render, viewWillAppear)
